import {
  Alert,
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  ExpandableSection,
  FormField,
  Grid,
  Header,
  Input,
  Link,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  Tabs,
  Textarea,
} from '@cloudscape-design/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import {
  getVoiceAgentDashboard,
  getVoiceAgentOptions,
  getVoiceAgentScorecardPreview,
  listVoiceAgentRevisionCalls,
  listVoiceAgentScorecards,
  promoteVoiceAgentRevision,
  saveVoiceAgentRevision,
  syncVoiceAgent,
  type VoiceAgentCall,
  type VoiceAgentOption,
  type VoiceAgentRevision,
  type VoiceAgentScorecard,
} from '../../common/api/voiceAgent';
import { createVoiceTestCall } from '../../common/api/voiceTestCalls';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { formatDuration, formatStatusLabel, statusType } from './presentation';

type AgentConfig = {
  prompt: string;
  first_message: string;
  scheduler_link_url: string;
  voice_id: string;
  tts_model: string;
  llm_model: string;
  temperature: number;
  speed: number;
  stability: number;
  similarity_boost: number;
  optimize_streaming_latency: number;
  turn_timeout_seconds: number;
  turn_eagerness: string;
  initial_wait_seconds: number;
  silence_timeout_seconds: number;
};

const EMPTY_CONFIG: AgentConfig = {
  prompt: '',
  first_message: '',
  scheduler_link_url: '',
  voice_id: '',
  tts_model: '',
  llm_model: '',
  temperature: 0.3,
  speed: 0.98,
  stability: 0.62,
  similarity_boost: 0.75,
  optimize_streaming_latency: 2,
  turn_timeout_seconds: 7,
  turn_eagerness: 'normal',
  initial_wait_seconds: 1,
  silence_timeout_seconds: 20,
};

const DEFAULT_TEST_PHONE_NUMBER = '+19403265175';

function stringValue(config: Record<string, unknown>, key: keyof AgentConfig): string {
  const value = config[key];
  return typeof value === 'string' ? value : String(EMPTY_CONFIG[key]);
}

function numberValue(config: Record<string, unknown>, key: keyof AgentConfig): number {
  const value = config[key];
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : Number(EMPTY_CONFIG[key]);
}

function configFromRevision(revision?: VoiceAgentRevision | null): AgentConfig {
  if (!revision) return EMPTY_CONFIG;
  const config = revision.config;
  return {
    prompt: stringValue(config, 'prompt'),
    first_message: stringValue(config, 'first_message'),
    scheduler_link_url: stringValue(config, 'scheduler_link_url'),
    voice_id: stringValue(config, 'voice_id'),
    tts_model: stringValue(config, 'tts_model'),
    llm_model: stringValue(config, 'llm_model'),
    temperature: numberValue(config, 'temperature'),
    speed: numberValue(config, 'speed'),
    stability: numberValue(config, 'stability'),
    similarity_boost: numberValue(config, 'similarity_boost'),
    optimize_streaming_latency: numberValue(config, 'optimize_streaming_latency'),
    turn_timeout_seconds: numberValue(config, 'turn_timeout_seconds'),
    turn_eagerness: stringValue(config, 'turn_eagerness'),
    initial_wait_seconds: numberValue(config, 'initial_wait_seconds'),
    silence_timeout_seconds: numberValue(config, 'silence_timeout_seconds'),
  };
}

function metric(revision: VoiceAgentRevision | null | undefined, key: string): number {
  return revision?.metrics?.[key] ?? 0;
}

function option(items: VoiceAgentOption[], id: string) {
  const found = items.find((item) => item.id === id);
  return id
    ? {
        label: found?.name || found?.id || id,
        value: id,
        description: found?.category || undefined,
      }
    : null;
}

function options(items: VoiceAgentOption[]) {
  return items.map((item) => ({
    label: item.name || item.id,
    value: item.id,
    description: item.deprecated ? 'Deprecated' : item.category,
  }));
}

function NumericField({
  label,
  description,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  description: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}) {
  return (
    <FormField label={label} description={description}>
      <Input
        type="number"
        value={String(value)}
        inputMode="decimal"
        step={step}
        onChange={({ detail }) => {
          const parsed = Number(detail.value);
          if (Number.isFinite(parsed)) onChange(Math.min(max, Math.max(min, parsed)));
        }}
      />
    </FormField>
  );
}

function RevisionSummary({
  label,
  revision,
  tone,
}: {
  label: string;
  revision: VoiceAgentRevision | null | undefined;
  tone: 'success' | 'in-progress';
}) {
  return (
    <Container
      header={
        <Header
          variant="h2"
          description={
            revision
              ? `Provider version ${revision.provider_version_id.slice(0, 12)}`
              : 'Not initialized'
          }
        >
          {label}
        </Header>
      }
    >
      {revision ? (
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Revision</Box>
            <Box variant="strong">#{revision.revision_number}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Status</Box>
            <StatusIndicator type={tone}>{formatStatusLabel(revision.sync_status)}</StatusIndicator>
          </div>
          <div>
            <Box variant="awsui-key-label">Calls</Box>
            <Box>{metric(revision, 'calls')}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Completed tests</Box>
            <Box>{metric(revision, 'completed')}</Box>
          </div>
        </ColumnLayout>
      ) : (
        <Box color="text-body-secondary">
          Initialize the agent from Advanced settings to create this lane.
        </Box>
      )}
    </Container>
  );
}

function CallStatus({ call }: { call: VoiceAgentCall }) {
  const verified =
    Boolean(call.actual_provider_branch_id) && Boolean(call.actual_provider_version_id);
  return (
    <SpaceBetween size="xxs">
      <StatusIndicator type={statusType(call.status)}>{formatStatusLabel(call.status)}</StatusIndicator>
      <Box variant="small" color={verified ? 'text-status-success' : 'text-body-secondary'}>
        {verified ? 'Provider revision verified' : 'Awaiting provider revision'}
      </Box>
    </SpaceBetween>
  );
}

function useCompactTables(): boolean {
  const [compact, setCompact] = useState(() => window.innerWidth < 720);
  useEffect(() => {
    const update = () => setCompact(window.innerWidth < 720);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return compact;
}

export default function VoiceAgentPage() {
  const queryClient = useQueryClient();
  const compactTables = useCompactTables();
  const dashboardQuery = useQuery({
    queryKey: ['voice-agent'],
    queryFn: getVoiceAgentDashboard,
    staleTime: 15_000,
  });
  const optionsQuery = useQuery({
    queryKey: ['voice-agent-options'],
    queryFn: getVoiceAgentOptions,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const scorecardsQuery = useQuery({
    queryKey: ['voice-agent-scorecards'],
    queryFn: () => listVoiceAgentScorecards(),
    staleTime: 60_000,
  });

  const dashboard = dashboardQuery.data;
  const testing = dashboard?.testing_revision;
  const [config, setConfig] = useState<AgentConfig>(EMPTY_CONFIG);
  const [loadedRevisionUuid, setLoadedRevisionUuid] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [promotionNote, setPromotionNote] = useState('');
  const [selectedScorecard, setSelectedScorecard] = useState<VoiceAgentScorecard | null>(null);
  const [phoneNumber, setPhoneNumber] = useState(DEFAULT_TEST_PHONE_NUMBER);
  const [confirmed, setConfirmed] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<VoiceAgentRevision[]>([]);

  useEffect(() => {
    if (testing?.uuid && testing.uuid !== loadedRevisionUuid) {
      setConfig(configFromRevision(testing));
      setLoadedRevisionUuid(testing.uuid);
      setChangeNote('');
    }
  }, [loadedRevisionUuid, testing]);

  const callsQuery = useQuery({
    queryKey: ['voice-agent-revision-calls', testing?.uuid],
    queryFn: () => listVoiceAgentRevisionCalls(testing?.uuid ?? ''),
    enabled: Boolean(testing?.uuid),
    refetchInterval: (query) => {
      const calls = query.state.data;
      return calls?.some((call) => ['queued', 'running', 'provider_initiated'].includes(call.status))
        ? 5_000
        : 15_000;
    },
  });
  const historyCallsQuery = useQuery({
    queryKey: ['voice-agent-revision-calls', selectedHistory[0]?.uuid],
    queryFn: () => listVoiceAgentRevisionCalls(selectedHistory[0]?.uuid ?? ''),
    enabled: Boolean(selectedHistory[0]?.uuid),
  });
  useEffect(() => {
    if (!testing || !callsQuery.data) return;
    const completedCalls = callsQuery.data.filter((call) => call.status === 'completed').length;
    if (completedCalls !== metric(testing, 'completed')) {
      void queryClient.invalidateQueries({ queryKey: ['voice-agent'] });
    }
  }, [callsQuery.data, queryClient, testing]);
  const previewQuery = useQuery({
    queryKey: [
      'voice-agent-scorecard-preview',
      selectedScorecard?.report_id,
      testing?.uuid,
    ],
    queryFn: () => getVoiceAgentScorecardPreview(selectedScorecard?.report_id ?? 0),
    enabled: Boolean(selectedScorecard?.report_id && testing?.uuid),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const refreshAgent = async () => {
    await queryClient.invalidateQueries({ queryKey: ['voice-agent'] });
    await queryClient.invalidateQueries({ queryKey: ['voice-agent-revision-calls'] });
  };

  const syncMutation = useMutation({
    mutationFn: syncVoiceAgent,
    onSuccess: async () => {
      await refreshAgent();
    },
  });
  const saveMutation = useMutation({
    mutationFn: () =>
      saveVoiceAgentRevision(
        config,
        testing?.provider_version_id ?? '',
        changeNote.trim(),
      ),
    onSuccess: async () => {
      await refreshAgent();
    },
  });
  const promoteMutation = useMutation({
    mutationFn: () => promoteVoiceAgentRevision(testing?.uuid ?? '', promotionNote.trim()),
    onSuccess: async () => {
      setPromotionNote('');
      await refreshAgent();
    },
  });
  const testCallMutation = useMutation({
    mutationFn: () => {
      if (!selectedScorecard) throw new Error('Choose a scorecard');
      return createVoiceTestCall(selectedScorecard.report_id, {
        target_phone_number: phoneNumber,
        request_id: crypto.randomUUID(),
        confirmation: 'AUTHORIZED_INTERNAL_TEST_CALL',
      });
    },
    onSuccess: async () => {
      setConfirmed(false);
      await refreshAgent();
    },
  });

  const scorecardOptions = useMemo(
    () =>
      (scorecardsQuery.data ?? []).map((scorecard) => ({
        label: scorecard.business_name,
        value: String(scorecard.report_id),
        description: scorecard.business_address ?? 'Address unavailable',
      })),
    [scorecardsQuery.data],
  );
  const hasUnsavedChanges = Boolean(
    testing &&
      JSON.stringify(config) !== JSON.stringify(configFromRevision(testing)),
  );
  const saveGuidance = !testing
    ? 'Initialize the agent before editing.'
    : !hasUnsavedChanges
      ? 'Change a setting to create a new Testing revision.'
      : !changeNote.trim()
        ? 'Unsaved changes · Add a change note to enable saving.'
        : 'Unsaved changes · Ready to save as a new Testing revision.';
  const canPromote = Boolean(
    testing &&
      metric(testing, 'completed') > 0 &&
      promotionNote.trim() &&
      !promoteMutation.isPending,
  );

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Voice Sales', href: '/dashboard/voice-sales' },
        { text: 'Agent', href: '/dashboard/voice-sales/agent' },
      ]}
      header={
        <Header
          variant="h1"
          description="Edit and test a safe draft, then deliberately promote the proven revision to Live."
        >
          Agent
        </Header>
      }
    >
      <SpaceBetween size="l">
        {dashboardQuery.isError ? (
          <Alert type="error" header="The agent could not be loaded" action={<Button onClick={() => dashboardQuery.refetch()}>Retry</Button>}>
            {dashboardQuery.error.message}
          </Alert>
        ) : null}
        {!dashboardQuery.isPending && dashboard && !dashboard.initialized ? (
          <Alert type="info" header="Initialize version control">
            Open Advanced settings at the bottom of this page to import the provider’s current Main
            configuration and create the isolated RTF Testing branch. Initialization does not place
            a call.
          </Alert>
        ) : null}

        <Grid gridDefinition={[{ colspan: { default: 12, m: 6 } }, { colspan: { default: 12, m: 6 } }]}>
          <RevisionSummary label="Live" revision={dashboard?.live_revision} tone="success" />
          <RevisionSummary label="Testing" revision={testing} tone="in-progress" />
        </Grid>

        <Grid gridDefinition={[{ colspan: { default: 12, m: 8 } }, { colspan: { default: 12, m: 4 } }]}>
          <Container
            header={
              <Header
                variant="h2"
                description={
                  testing
                    ? `Editing Testing revision #${testing.revision_number}. Typing does not save automatically.`
                    : 'Initialize from ElevenLabs before editing.'
                }
              >
                Testing configuration
              </Header>
            }
            footer={
              <SpaceBetween size="s">
                <FormField
                  label="Change note"
                  description="Required when saving so revision history explains what changed."
                  constraintText="Changes stay in this form until you save a new Testing revision."
                >
                  <Input
                    value={changeNote}
                    placeholder="Example: Shorter opening and warmer delivery"
                    onChange={({ detail }) => setChangeNote(detail.value)}
                  />
                </FormField>
                <StatusIndicator
                  type={
                    hasUnsavedChanges
                      ? changeNote.trim()
                        ? 'in-progress'
                        : 'warning'
                      : 'stopped'
                  }
                >
                  {saveGuidance}
                </StatusIndicator>
                {saveMutation.isError ? (
                  <Alert type="error" header="Testing revision was not saved">
                    {saveMutation.error.message}
                  </Alert>
                ) : null}
                {saveMutation.isSuccess && !hasUnsavedChanges ? (
                  <Alert type="success">
                    Testing revision #{saveMutation.data.revision_number} was saved and is ready for
                    a test call.
                  </Alert>
                ) : null}
                <SpaceBetween direction="horizontal" size="s">
                  <Button
                    disabled={!testing || !hasUnsavedChanges}
                    onClick={() => {
                      if (testing) {
                        setConfig(configFromRevision(testing));
                        setChangeNote('');
                      }
                    }}
                  >
                    Discard changes
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!testing || !hasUnsavedChanges || !changeNote.trim()}
                    loading={saveMutation.isPending}
                    onClick={() => saveMutation.mutate()}
                  >
                    Save new Testing revision
                  </Button>
                </SpaceBetween>
              </SpaceBetween>
            }
          >
            <SpaceBetween size="l">
              <Tabs
                tabs={[
                  {
                    id: 'script',
                    label: 'Script',
                    content: (
                      <SpaceBetween size="l">
                        <FormField
                          label="Opening message"
                          description="The first thing the agent says after the call connects."
                        >
                          <Textarea
                            rows={4}
                            value={config.first_message}
                            onChange={({ detail }) =>
                              setConfig((current) => ({
                                ...current,
                                first_message: detail.value,
                              }))
                            }
                          />
                        </FormField>
                        <FormField
                          label="Agent prompt"
                          description="Keep the scorecard variables, booking-link behavior, and opt-out instructions intact."
                        >
                          <Textarea
                            rows={20}
                            value={config.prompt}
                            onChange={({ detail }) =>
                              setConfig((current) => ({ ...current, prompt: detail.value }))
                            }
                          />
                        </FormField>
                      </SpaceBetween>
                    ),
                  },
                  {
                    id: 'voice',
                    label: 'Voice & models',
                    content: (
                      <SpaceBetween size="l">
                        {optionsQuery.isError ? (
                          <Alert type="warning">
                            Provider options could not be loaded. Current values remain editable.
                          </Alert>
                        ) : null}
                        <FormField label="Voice" description="Applied to new calls on this revision.">
                          <Select
                            statusType={optionsQuery.isPending ? 'loading' : 'finished'}
                            selectedOption={option(optionsQuery.data?.voices ?? [], config.voice_id)}
                            options={options(optionsQuery.data?.voices ?? [])}
                            onChange={({ detail }) =>
                              setConfig((current) => ({
                                ...current,
                                voice_id: detail.selectedOption.value ?? '',
                              }))
                            }
                          />
                        </FormField>
                        <ColumnLayout columns={2}>
                          <FormField label="Voice model">
                            <Select
                              statusType={optionsQuery.isPending ? 'loading' : 'finished'}
                              selectedOption={option(
                                optionsQuery.data?.tts_models ?? [],
                                config.tts_model,
                              )}
                              options={options(optionsQuery.data?.tts_models ?? [])}
                              onChange={({ detail }) =>
                                setConfig((current) => ({
                                  ...current,
                                  tts_model: detail.selectedOption.value ?? '',
                                }))
                              }
                            />
                          </FormField>
                          <FormField label="Conversation model">
                            <Select
                              statusType={optionsQuery.isPending ? 'loading' : 'finished'}
                              selectedOption={option(
                                optionsQuery.data?.llms ?? [],
                                config.llm_model,
                              )}
                              options={options(optionsQuery.data?.llms ?? [])}
                              onChange={({ detail }) =>
                                setConfig((current) => ({
                                  ...current,
                                  llm_model: detail.selectedOption.value ?? '',
                                }))
                              }
                            />
                          </FormField>
                        </ColumnLayout>
                        <ColumnLayout columns={2}>
                          <NumericField label="Speaking speed" description="0.7 slow · 1.0 natural · 1.2 fast" value={config.speed} min={0.7} max={1.2} step={0.01} onChange={(speed) => setConfig((current) => ({ ...current, speed }))} />
                          <NumericField label="Stability" description="Higher values sound more consistent." value={config.stability} min={0} max={1} step={0.01} onChange={(stability) => setConfig((current) => ({ ...current, stability }))} />
                          <NumericField label="Similarity" description="How closely the output follows the selected voice." value={config.similarity_boost} min={0} max={1} step={0.01} onChange={(similarity_boost) => setConfig((current) => ({ ...current, similarity_boost }))} />
                        </ColumnLayout>
                      </SpaceBetween>
                    ),
                  },
                  {
                    id: 'behavior',
                    label: 'Conversation',
                    content: (
                      <SpaceBetween size="l">
                        <ColumnLayout columns={2}>
                          <NumericField label="Creativity" description="Conversation-model temperature." value={config.temperature} min={0} max={1} step={0.05} onChange={(temperature) => setConfig((current) => ({ ...current, temperature }))} />
                          <FormField label="Turn eagerness" description="How quickly the agent responds.">
                            <Select
                              selectedOption={{ label: formatStatusLabel(config.turn_eagerness), value: config.turn_eagerness }}
                              options={['patient', 'normal', 'eager'].map((value) => ({ label: formatStatusLabel(value), value }))}
                              onChange={({ detail }) => setConfig((current) => ({ ...current, turn_eagerness: detail.selectedOption.value ?? 'normal' }))}
                            />
                          </FormField>
                          <NumericField label="Turn timeout" description="Seconds before the agent treats a turn as complete." value={config.turn_timeout_seconds} min={1} max={30} step={1} onChange={(turn_timeout_seconds) => setConfig((current) => ({ ...current, turn_timeout_seconds }))} />
                          <NumericField label="Initial wait" description="Seconds before the opening message." value={config.initial_wait_seconds} min={0} max={10} step={0.1} onChange={(initial_wait_seconds) => setConfig((current) => ({ ...current, initial_wait_seconds }))} />
                          <NumericField label="Silence timeout" description="Seconds of silence before the call ends safely." value={config.silence_timeout_seconds} min={5} max={120} step={1} onChange={(silence_timeout_seconds) => setConfig((current) => ({ ...current, silence_timeout_seconds }))} />
                          <NumericField label="Streaming latency" description="Provider optimization level from 0 through 4." value={config.optimize_streaming_latency} min={0} max={4} step={1} onChange={(optimize_streaming_latency) => setConfig((current) => ({ ...current, optimize_streaming_latency }))} />
                        </ColumnLayout>
                      </SpaceBetween>
                    ),
                  },
                  {
                    id: 'follow-up',
                    label: 'Follow-up',
                    content: (
                      <FormField
                        label="Booking link"
                        description="The exact HTTPS URL sent when the prospect asks to schedule."
                      >
                        <Input
                          type="url"
                          value={config.scheduler_link_url}
                          placeholder="https://scheduler.zoom.us/..."
                          onChange={({ detail }) =>
                            setConfig((current) => ({
                              ...current,
                              scheduler_link_url: detail.value,
                            }))
                          }
                        />
                      </FormField>
                    ),
                  },
                ]}
              />
            </SpaceBetween>
          </Container>

          <SpaceBetween size="l">
            <Container
              header={
                <Header
                  variant="h2"
                  description="Calls from here always use the current Testing revision."
                >
                  Place a test call
                </Header>
              }
            >
              <SpaceBetween size="l">
                <FormField label="Scorecard">
                  <Select
                    filteringType="auto"
                    statusType={scorecardsQuery.isPending ? 'loading' : 'finished'}
                    placeholder="Choose a business"
                    selectedOption={
                      selectedScorecard
                        ? {
                            label: selectedScorecard.business_name,
                            value: String(selectedScorecard.report_id),
                            description: selectedScorecard.business_address ?? undefined,
                          }
                        : null
                    }
                    options={scorecardOptions}
                    onChange={({ detail }) => {
                      const reportId = Number(detail.selectedOption.value);
                      setConfirmed(false);
                      setSelectedScorecard(
                        scorecardsQuery.data?.find(
                          (scorecard) => scorecard.report_id === reportId,
                        ) ?? null,
                      );
                    }}
                  />
                </FormField>
                {selectedScorecard ? (
                  <ExpandableSection
                    defaultExpanded
                    headerText={
                      previewQuery.data
                        ? `Preview Testing revision #${previewQuery.data.testing_revision_number}`
                        : 'Preview Testing script'
                    }
                  >
                    <SpaceBetween size="m">
                      {hasUnsavedChanges ? (
                        <Alert type="warning">
                          This preview uses the saved Testing revision. Save or discard your editor
                          changes before placing the call.
                        </Alert>
                      ) : null}
                      {previewQuery.isPending ? (
                        <StatusIndicator type="loading">
                          Rendering the selected scorecard
                        </StatusIndicator>
                      ) : null}
                      {previewQuery.isError ? (
                        <Alert
                          type="error"
                          header="The call script could not be previewed"
                          action={<Button onClick={() => previewQuery.refetch()}>Retry</Button>}
                        >
                          {previewQuery.error.message}
                        </Alert>
                      ) : null}
                      {previewQuery.data ? (
                        <>
                        {(previewQuery.data.unresolved_variables ?? []).length > 0 ? (
                            <Alert type="warning">
                              Unresolved template values:{' '}
                              {(previewQuery.data.unresolved_variables ?? []).join(', ')}
                            </Alert>
                          ) : null}
                          <FormField label="Rendered opening message">
                            <Textarea
                              readOnly
                              rows={4}
                              value={previewQuery.data.rendered_first_message}
                            />
                          </FormField>
                          <FormField label="Rendered agent prompt">
                            <Textarea
                              readOnly
                              rows={12}
                              value={previewQuery.data.rendered_prompt}
                            />
                          </FormField>
                        </>
                      ) : null}
                    </SpaceBetween>
                  </ExpandableSection>
                ) : null}
                <FormField
                  label="Phone number"
                  description="Enter any number you are authorized to call."
                >
                  <Input
                    value={phoneNumber}
                    inputMode="tel"
                    placeholder="+1 940 326 5175"
                    onChange={({ detail }) => setPhoneNumber(detail.value)}
                  />
                </FormField>
                <Checkbox
                  checked={confirmed}
                  disabled={
                    hasUnsavedChanges ||
                    !previewQuery.data ||
                    previewQuery.isError ||
                    previewQuery.isPending
                  }
                  onChange={({ detail }) => setConfirmed(detail.checked)}
                >
                  I confirm this is an authorized internal test call.
                </Checkbox>
                <Button
                  variant="primary"
                  fullWidth
                  loading={testCallMutation.isPending}
                  disabled={
                    !testing ||
                    !selectedScorecard ||
                    !previewQuery.data ||
                    previewQuery.isError ||
                    previewQuery.isPending ||
                    hasUnsavedChanges ||
                    !phoneNumber.trim() ||
                    !confirmed
                  }
                  onClick={() => testCallMutation.mutate()}
                >
                  Call with Testing
                </Button>
                {testCallMutation.isError ? (
                  <Alert type="error" header="Test call was not queued">
                    {testCallMutation.error.message}
                  </Alert>
                ) : null}
                {testCallMutation.isSuccess ? (
                  <Alert type="success">
                    Test call queued to{' '}
                    {testCallMutation.data.call.target_phone_number_redacted}.
                  </Alert>
                ) : null}
              </SpaceBetween>
            </Container>
            <Container header={<Header variant="h2">Promote to Live</Header>}>
              <SpaceBetween size="m">
                <Alert type={metric(testing, 'completed') > 0 ? 'success' : 'info'}>
                  {metric(testing, 'completed') > 0
                    ? `${metric(testing, 'completed')} completed call${metric(testing, 'completed') === 1 ? ' proves' : 's prove'} this Testing revision.`
                    : 'Complete at least one test call on this exact revision before promotion.'}
                </Alert>
                <FormField label="Promotion note">
                  <Input
                    value={promotionNote}
                    placeholder="Why this revision is ready"
                    onChange={({ detail }) => setPromotionNote(detail.value)}
                  />
                </FormField>
                <Button
                  fullWidth
                  disabled={!canPromote}
                  loading={promoteMutation.isPending}
                  onClick={() => promoteMutation.mutate()}
                >
                  Promote proven revision
                </Button>
                {promoteMutation.isError ? (
                  <Alert type="error" header="Testing revision was not promoted">
                    {promoteMutation.error.message}
                  </Alert>
                ) : null}
                {promoteMutation.isSuccess ? (
                  <Alert type="success">
                    Revision #{promoteMutation.data.revision_number} is now Live. Testing was rebased
                    from it.
                  </Alert>
                ) : null}
              </SpaceBetween>
            </Container>
          </SpaceBetween>
        </Grid>

        <Container>
          <Table
            variant="embedded"
            header={
              <Header variant="h2" counter={`(${callsQuery.data?.length ?? 0})`}>
                Calls on current Testing revision
              </Header>
            }
            loading={callsQuery.isPending}
            loadingText="Loading Testing calls"
            empty={
              <Box textAlign="center" color="inherit">
                <b>No calls on this revision</b>
                <Box variant="p" color="inherit">Place a test call to create its first evidence record.</Box>
              </Box>
            }
            items={callsQuery.data ?? []}
            columnDefinitions={
              compactTables
                ? [
                    {
                      id: 'call',
                      header: 'Call',
                      cell: (call) => (
                        <SpaceBetween size="xxs">
                          <CallStatus call={call} />
                          <Box>{call.target_phone_number_redacted}</Box>
                        </SpaceBetween>
                      ),
                    },
                    {
                      id: 'result',
                      header: 'Result',
                      cell: (call) => (
                        <SpaceBetween size="xxs">
                          <Box>{formatStatusLabel(call.outcome)}</Box>
                          <Box variant="small" color="text-body-secondary">
                            {formatDuration(call.duration_seconds)} ·{' '}
                            {formatDateStringWithTime(call.created_at)}
                          </Box>
                        </SpaceBetween>
                      ),
                    },
                  ]
                : [
                    {
                      id: 'status',
                      header: 'Status & verification',
                      cell: (call) => <CallStatus call={call} />,
                    },
                    {
                      id: 'target',
                      header: 'Target',
                      cell: (call) => (
                        <SpaceBetween size="xxs">
                          <Box>{call.target_phone_number_redacted}</Box>
                          <Box variant="small" color="text-body-secondary">
                            {formatStatusLabel(call.call_type)} call
                          </Box>
                        </SpaceBetween>
                      ),
                    },
                    {
                      id: 'result',
                      header: 'Result',
                      cell: (call) => (
                        <SpaceBetween size="xxs">
                          <Box>{formatStatusLabel(call.outcome)}</Box>
                          <Box variant="small" color="text-body-secondary">
                            {formatDuration(call.duration_seconds)}
                          </Box>
                        </SpaceBetween>
                      ),
                    },
                    {
                      id: 'created',
                      header: 'Started',
                      cell: (call) => formatDateStringWithTime(call.created_at),
                    },
                  ]
            }
          />
        </Container>

        <Container>
          <Table
            variant="embedded"
            selectionType="single"
            selectedItems={selectedHistory}
            onSelectionChange={({ detail }) => setSelectedHistory(detail.selectedItems)}
            header={<Header variant="h2">Revision history</Header>}
            items={dashboard?.revisions ?? []}
            trackBy="uuid"
            empty={<Box textAlign="center">No revisions imported yet.</Box>}
            columnDefinitions={
              compactTables
                ? [
                    {
                      id: 'revision-summary',
                      header: 'Revision',
                      cell: (revision) => {
                        const snapshot = configFromRevision(revision);
                        return (
                          <SpaceBetween size="xxs">
                            <Box variant="strong">#{revision.revision_number}</Box>
                            <StatusIndicator
                              type={
                                revision.lifecycle === 'live' ? 'success' : 'in-progress'
                              }
                            >
                              {formatStatusLabel(revision.lifecycle)}
                            </StatusIndicator>
                            <Box variant="small" color="text-body-secondary">
                              {snapshot.voice_id}
                            </Box>
                            <Box variant="small" color="text-body-secondary">
                              {snapshot.tts_model} · {snapshot.llm_model}
                            </Box>
                            <Box variant="small" color="text-body-secondary">
                              {metric(revision, 'calls')} calls ·{' '}
                              {metric(revision, 'completed')} completed
                            </Box>
                            <Box variant="small" color="text-body-secondary">
                              {formatDateStringWithTime(revision.created_at)}
                            </Box>
                          </SpaceBetween>
                        );
                      },
                    },
                  ]
                : [
                    {
                      id: 'revision',
                      header: 'Revision',
                      cell: (revision) => (
                        <SpaceBetween size="xxs">
                          <Box variant="strong">#{revision.revision_number}</Box>
                          <StatusIndicator
                            type={
                              revision.lifecycle === 'live' ? 'success' : 'in-progress'
                            }
                          >
                            {formatStatusLabel(revision.lifecycle)}
                          </StatusIndicator>
                        </SpaceBetween>
                      ),
                    },
                    {
                      id: 'configuration',
                      header: 'Voice & models',
                      cell: (revision) => {
                        const snapshot = configFromRevision(revision);
                        return (
                          <SpaceBetween size="xxs">
                            <Box>{snapshot.voice_id}</Box>
                            <Box variant="small" color="text-body-secondary">
                              {snapshot.tts_model} · {snapshot.llm_model}
                            </Box>
                          </SpaceBetween>
                        );
                      },
                    },
                    {
                      id: 'evidence',
                      header: 'Evidence',
                      cell: (revision) => (
                        <SpaceBetween size="xxs">
                          <Box>
                            {metric(revision, 'calls')} calls ·{' '}
                            {metric(revision, 'completed')} completed
                          </Box>
                          <Box variant="small" color="text-body-secondary">
                            {metric(revision, 'scheduler_links')} booking links sent
                          </Box>
                        </SpaceBetween>
                      ),
                    },
                    {
                      id: 'created',
                      header: 'Created',
                      cell: (revision) => (
                        <SpaceBetween size="xxs">
                          <Box>{formatDateStringWithTime(revision.created_at)}</Box>
                          <Box variant="small" color="text-body-secondary">
                            {revision.change_note}
                          </Box>
                        </SpaceBetween>
                      ),
                    },
                  ]
            }
          />
          {selectedHistory[0] ? (
            <ExpandableSection
              defaultExpanded
              headerText={`Revision #${selectedHistory[0].revision_number} snapshot`}
            >
              <SpaceBetween size="s">
                <ColumnLayout columns={3} variant="text-grid">
                  <div><Box variant="awsui-key-label">Booking link</Box><Link href={configFromRevision(selectedHistory[0]).scheduler_link_url} external>{configFromRevision(selectedHistory[0]).scheduler_link_url}</Link></div>
                  <div><Box variant="awsui-key-label">Speed / stability</Box><Box>{configFromRevision(selectedHistory[0]).speed} / {configFromRevision(selectedHistory[0]).stability}</Box></div>
                  <div><Box variant="awsui-key-label">Provider version</Box><Box>{selectedHistory[0].provider_version_id}</Box></div>
                </ColumnLayout>
                <Box variant="awsui-key-label">Opening message</Box>
                <Box>{configFromRevision(selectedHistory[0]).first_message}</Box>
                <Box variant="awsui-key-label">Prompt</Box>
                <Box>{configFromRevision(selectedHistory[0]).prompt}</Box>
                <Table
                  variant="embedded"
                  header={
                    <Header
                      variant="h3"
                      counter={`(${historyCallsQuery.data?.length ?? 0})`}
                    >
                      Calls using this revision
                    </Header>
                  }
                  loading={historyCallsQuery.isPending}
                  loadingText="Loading revision calls"
                  items={historyCallsQuery.data ?? []}
                  empty={<Box textAlign="center">No calls used this revision.</Box>}
                  columnDefinitions={
                    compactTables
                      ? [
                          {
                            id: 'call',
                            header: 'Call',
                            cell: (call) => (
                              <SpaceBetween size="xxs">
                                <CallStatus call={call} />
                                <Box>{call.target_phone_number_redacted}</Box>
                              </SpaceBetween>
                            ),
                          },
                          {
                            id: 'result',
                            header: 'Result',
                            cell: (call) => (
                              <SpaceBetween size="xxs">
                                <Box>{formatStatusLabel(call.outcome)}</Box>
                                <Box variant="small" color="text-body-secondary">
                                  {formatDuration(call.duration_seconds)} ·{' '}
                                  {formatDateStringWithTime(call.created_at)}
                                </Box>
                              </SpaceBetween>
                            ),
                          },
                        ]
                      : [
                          {
                            id: 'status',
                            header: 'Status',
                            cell: (call) => <CallStatus call={call} />,
                          },
                          {
                            id: 'target',
                            header: 'Target',
                            cell: (call) => call.target_phone_number_redacted,
                          },
                          {
                            id: 'result',
                            header: 'Outcome',
                            cell: (call) =>
                              `${formatStatusLabel(call.outcome)} · ${formatDuration(call.duration_seconds)}`,
                          },
                          {
                            id: 'created',
                            header: 'Started',
                            cell: (call) => formatDateStringWithTime(call.created_at),
                          },
                        ]
                  }
                />
              </SpaceBetween>
            </ExpandableSection>
          ) : null}
        </Container>

        <ExpandableSection headerText="Advanced settings">
          <SpaceBetween size="m">
            <Alert type="warning" header="Import provider configuration">
              This is only for initial setup or recovering an intentional change made directly in
              ElevenLabs. It replaces unsaved form edits with the current provider configuration.
              It does not place a call or promote Testing to Live.
            </Alert>
            <Box>
              <Button
                iconName="refresh"
                loading={syncMutation.isPending}
                onClick={() => syncMutation.mutate()}
              >
                {dashboard?.initialized
                  ? 'Reconcile from ElevenLabs'
                  : 'Initialize from ElevenLabs'}
              </Button>
            </Box>
            {syncMutation.isError ? (
              <Alert type="error" header="ElevenLabs configuration was not imported">
                {syncMutation.error.message}
              </Alert>
            ) : null}
            {syncMutation.isSuccess ? (
              <Alert type="success">
                ElevenLabs Main and Testing were imported as new local revisions.
              </Alert>
            ) : null}
          </SpaceBetween>
        </ExpandableSection>
      </SpaceBetween>
    </DashboardViewLayout>
  );
}
